import React, { useState, useRef, useEffect } from 'react';
import { CreateSpaceRequest } from '../../types';

interface SpaceDropdownMenuProps {
  postId: string;
  postTitle: string;
  onCreateSpace: (spaceData: CreateSpaceRequest) => Promise<string | null>;
  onClose: () => void;
  isOpen: boolean;
}

const SpaceDropdownMenu: React.FC<SpaceDropdownMenuProps> = ({
  postId,
  postTitle,
  onCreateSpace,
  onClose,
  isOpen
}) => {
  const [title, setTitle] = useState(postTitle ? `Discussion: ${postTitle.slice(0, 30)}...` : '');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [maxParticipants, setMaxParticipants] = useState(20);
  const [isCreating, setIsCreating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fermer le menu en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleCreateSpace = async () => {
    if (!title.trim()) return;

    setIsCreating(true);
    try {
      const spaceData: CreateSpaceRequest = {
        title: title.trim(),
        description: description.trim(),
        isPublic,
        maxParticipants,
        tags: [],
        postId
      };

      await onCreateSpace(spaceData);
      onClose();
    } catch (error) {
      console.error('Erreur création Space:', error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="mt-4 w-full bg-gradient-to-br from-emerald-900/20 via-gray-800/30 to-emerald-800/20 backdrop-blur-sm border border-emerald-800/40 rounded-xl p-4 transition-all duration-300 ease-in-out"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-base font-semibold text-emerald-100 flex items-center">
          🎙️ Créer un Space
        </h4>
        <button 
          onClick={onClose}
          className="text-emerald-300 hover:text-red-400 text-lg transition-colors"
        >
          ×
        </button>
      </div>

      {/* Formulaire compact */}
      <div className="space-y-3">
        {/* Titre */}
        <div>
          <label className="block text-emerald-200 text-xs font-medium mb-1">
            Titre du Space
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Discussion sur..."
            className="w-full bg-gray-700/50 border border-emerald-800/40 rounded-lg px-3 py-2 text-emerald-100 placeholder-emerald-300/60 focus:ring-1 focus:ring-cyan-400/50 focus:outline-none text-sm"
            maxLength={100}
          />
        </div>

        {/* Paramètres en ligne */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-emerald-200 text-xs font-medium mb-1">
              Participants max
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="5"
                max="50"
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                className="flex-1 h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-emerald-100 font-medium w-6 text-center text-xs">
                {maxParticipants}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-emerald-200 text-xs font-medium mb-1">
              Visibilité
            </label>
            <div className="flex space-x-2">
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                  className="text-emerald-600 focus:ring-emerald-500 w-3 h-3"
                />
                <span className="text-emerald-100 text-xs">🌍</span>
              </label>
              
              <label className="flex items-center space-x-1 cursor-pointer">
                <input
                  type="radio"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                  className="text-emerald-600 focus:ring-emerald-500 w-3 h-3"
                />
                <span className="text-emerald-100 text-xs">🔒</span>
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2 pt-2">
          <button 
            onClick={onClose}
            className="flex-1 py-2 px-3 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-700/70 transition text-xs"
            disabled={isCreating}
          >
            Annuler
          </button>
          <button 
            onClick={handleCreateSpace}
            disabled={!title.trim() || isCreating}
            className="flex-1 py-2 px-3 rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 text-white font-medium hover:from-emerald-700 hover:to-cyan-700 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs"
          >
            {isCreating ? (
              <span className="flex items-center justify-center">
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1"></div>
                Création...
              </span>
            ) : (
              '🚀 Créer'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpaceDropdownMenu;
